import { setUserDetail } from "@/store/slices/userDetail/userDetailSlice";
import { setBrands } from "@/store/slices/brands/brandsSlice";
import { setMarkets } from "@/store/slices/markets/marketsSlice";
import userService from "@/endpoints/userService";
import dataService from "@/endpoints/dataService";
import type { AppDispatch } from "../store";
import type { UserDetail } from "../slices/userDetail/userDetail.types";
import { setBootstrapDataStatus } from "../slices/bootstrapDataStatus/bootstrapDataStatusSlice";
import { setBrandsMapping } from "../slices/brandsOrderMapping/brandsOrderMappingSlice";
import { setFeatureStatusInitially } from "../slices/featureStatus/featureStatusSlice";
import { setDistributionsCatalog } from "../slices/distributionsCatalog/distributionsCatalogSlice";
import { warnIfCatalogLacksApplicability } from "../slices/distributionsCatalog/catalogSelectors";
import { setQuestionsCatalog } from "../slices/questionsCatalog/questionsCatalogSlice";
import { setFiltersCatalog } from "../slices/filters/filtersSlice";
import { setMetricsCatalog } from "../slices/metricsCatalog/metricsCatalogSlice";

const getUserDetail = () => async (dispatch: AppDispatch) => {
  try {
    const response: any = await userService.bootstrap();
    if (response.user) {
      dispatch(
        setUserDetail({
          id: response?.user?.userId,
          name: response?.user?.name,
          locale: response?.user?.locale,
          timezone: response?.user?.timezone,
          email: response?.user?.email,
          client: response?.user?.client,
          isAdmin: response?.user?.auth?.roles?.includes("qb.admin"),
        })
      );
    }
    if (response?.brands?.length > 0) {
      dispatch(setBrands(response.brands));
    }

    if (response?.featureStatus) {
      dispatch(setFeatureStatusInitially(response?.featureStatus));
    }

    if (response.marketData) {
      dispatch(
        setMarkets({
          markets: response?.marketData?.markets,
          groups: response?.marketData?.groups,
        })
      );
    }
    if (response?.brandsOrderMapping) {
      dispatch(setBrandsMapping(response?.brandsOrderMapping));
    }
    warnIfCatalogLacksApplicability(response?.distributions ?? null);
    dispatch(setDistributionsCatalog(response?.distributions ?? null));
    dispatch(setQuestionsCatalog(response?.questions ?? null));
    dispatch(setFiltersCatalog(response?.filters ?? null));

    // ── Metric catalog: hard dependency, fetched with accessible market ids ──
    // Bootstrap market objects are normalized by prepareMarketData
    // (src/utils/bootstrapApi.ts) and carry `id`, not `marketId` — see
    // Market.id in marketSlice.types.ts.
    let metricsCatalogOk = false;
    const marketIdsForCatalog: number[] = Array.from(
      new Set(
        (response?.marketData?.markets ?? []).map((m: any) => m.id),
      ),
    );
    if (marketIdsForCatalog.length > 0) {
      const catalogResult: any = await dataService.metricsCatalog(marketIdsForCatalog);
      if (catalogResult?.response) {
        dispatch(setMetricsCatalog(catalogResult.response));
        metricsCatalogOk = true;
      }
    }

    if (
      response?.brands?.length > 0 &&
      response?.marketData?.markets?.length > 0 &&
      metricsCatalogOk
    ) {
      dispatch(
        setBootstrapDataStatus({
          isLoading: false,
          isDataFetched: true,
          isContractExpired: false,
          havingError: false,
          errorMessage: "",
        } as any)
      );
    } else if (
      response?.brands?.length > 0 &&
      response?.marketData?.markets?.length > 0 &&
      !metricsCatalogOk
    ) {
      dispatch(
        setBootstrapDataStatus({
          isLoading: false,
          isDataFetched: false,
          isContractExpired: false,
          havingError: true,
          errorMessage: "",
        } as any)
      );
    } else {
      dispatch(
        setBootstrapDataStatus({
          isLoading: false,
          isDataFetched: false,
          isContractExpired: true,
          havingError: false,
          errorMessage: "",
        } as any)
      );
    }
    return true;
  } catch (error) {
    dispatch(
      setBootstrapDataStatus({
        isLoading: false,
        isDataFetched: false,
        isContractExpired: false,
        havingError: true,
        errorMessage: "",
      } as any)
    );
    console.warn("Error fetching user detail", error);
  }
};

export default getUserDetail;
